/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author Shihan Rahim
 * 01/24/2023
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the lms form
 */
class LmsUrlTypeForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            lmsUrlTypeId: null,
            lmsUrlTypeName: '',
            description:'',
            createdDate: '',
            modifiedDate: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'lmsUrlTypeName',
                    placeholder: 'Please enter LMS URL Type Name here',
                    value: '',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Description *',
                    inputName: 'description',
                    placeholder: 'Please enter Description here',
                    value: '',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const lmsUrlTypeId = this.props.match.params.id;

        // set icons to the header
        if (lmsUrlTypeId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'lmsUrlTypes',
                pageName: "Lms Url Types",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/lmsUrlTypes",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'lmsUrlTypes',
                pageName: "Lms Url Types",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/lmsUrlTypes",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteLmsUrl);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const lmsUrlTypeId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create lms
        if (lmsUrlTypeId !== undefined) {

            const url = '/lms-url-types?search=%7BlmsUrlTypeId:' + lmsUrlTypeId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.lmsUrlTypeId = (data.lmsUrlTypeId !== undefined) ? data.lmsUrlTypeId : null;
                    oldState.lmsUrlTypeName = (data.lmsUrlTypeName !== undefined) ? data.lmsUrlTypeName : null;
                    oldState.description = (data.description !== undefined) ? data.description : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                })
                .catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteLmsUrl);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel lms url type async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.lmsUrlTypeId !== null) {
            data = {
                lmsUrlTypeName: this.state.lmsUrlTypeName,
                description: this.state.description,
                lmsUrlTypeId: Number(this.state.lmsUrlTypeId)
            };
        } else {
            data = {
                lmsUrlTypeName: this.state.lmsUrlTypeName,
                description: this.state.description
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.lmsUrlTypeId !== null) {
                this.updateLmsUrlTypes(data);
            } else {
                this.createLmsUrlTypes(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'lmsUrlTypeName':
                if (value === '')
                    error[key] = "LMS URL Type Name is Required!";
                else if (value === ' ')
                    error[key] = "LMS URL Type Name can't be an empty space!";
                else
                    error[key] = "";
                break;
            case 'description':
                if (value === '')
                    error[key] = "LMS URL Type Description is Required!";
                else if (value === ' ')
                    error[key] = "LMS URL Type Description can't be an empty space!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create lms url type
     * @param data - the data to create a new lms url type
     */
    createLmsUrlTypes = async (data) => {
        await API.post('/lms-url-types',
            {
                "lmsUrlTypeName": data.lmsUrlTypeName,
                "description": data.description
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Lms Url Type");
            this.props.history.push('/home/lmsUrlTypes');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update lmsUrlTypes
     * @param data - updated lmsUrlTypes details
     */
    updateLmsUrlTypes = async (data) => {
        await API.put('/lms-url-types',
            {
                "lmsUrlTypeId": data.lmsUrlTypeId,
                "lmsUrlTypeName": data.lmsUrlTypeName,
                "description": data.description
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Lms Url Type");
            this.props.history.push('/home/lmsUrlTypes');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.lmsUrlId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'lmsUrlTypes',
                name: this.state.lmsUrlTypeName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Lms Url Type",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a lmsUrlTypes
     * @param data
     */
    deleteLmsUrl = async (data) => {
        if (data && (this.props.match.path === "/home/lmsUrlTypes/:id")) {
            await API.delete('/lms-url-types?lmsUrlTypeId=' + this.state.lmsUrlTypeId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Lms Url Type");
                    this.props.history.push('/home/lmsUrlTypes');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "LMS Url Type is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);
        
        newState.lmsUrlTypeName = "";
        newState.description = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/lmsUrlTypes');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default LmsUrlTypeForm;
